import _ from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQueryCache } from "react-query";
import { useSelector } from "react-redux";
import { useMedia } from "react-media";
import { useParams } from "react-router-dom";
import { Dimmer, Loader, Segment, Grid, Sticky, Button } from "semantic-ui-react";
import networkSelectors from "../../../selectors/network";
import { InspectionModal } from "../components/inspections/InspectionModal";
import { InspectionSection } from "../components/inspections/InspectionSection";
import { InspectionPanel } from "../components/inspections/InspectionPanel";
import PermitTabs from "../components/PermitTabs";
import "./InspectionChecklist.scss";
import useInspection from "../hooks/inspections/useInspection";
import { InspectionSectionType } from "../../queries/inspection-types/getInspectionType";
import { putInspectionFail } from "../api/inspections/putInspectionFail";
import { putInspectionPass } from "../api/inspections/putInspectionPass";
import useCountries from "../../../hooks/useCountries";
import { makeFullAddress } from "../../../utils/functions";
import StaffInspectionSummary from "../components/inspections/InspectionSummary/StaffInspectionSummary";
import useInspectionType from "../../tanstack/inspection-types/useInspectionType";
import useInspectionItemFailMutation from "../hooks/inspections/useInspectionItemFailMutation";
import useInspectionItemPassMutation from "../hooks/inspections/useInspectionItemPassMutation";
import { InspectionGetByIdRes } from "../api/inspections/getInspection";
import classNames from "classnames";

export const InspectionChecklist = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);

  const { agencyCountry } = useCountries();
  const { inspectionQuery } = useInspection({ id: id });

  const recordId = useMemo(() => _.get(inspectionQuery.data, "data.attributes.record-id"), [inspectionQuery.data]);
  const assigneeFirst = useMemo(
    () => _.get(inspectionQuery.data, "data.attributes.assignee.data.attributes.first-name"),
    [inspectionQuery.data],
  );
  const assigneeLast = useMemo(
    () => _.get(inspectionQuery.data, "data.attributes.assignee.data.attributes.last-name"),
    [inspectionQuery.data],
  );
  const assignee = `${assigneeFirst} ${assigneeLast}`;
  const inspectionTitle = useMemo(() => _.get(inspectionQuery.data, "data.attributes.title"), [inspectionQuery.data]);
  const scheduledAt = useMemo(
    () => _.get(inspectionQuery.data, "data.attributes.scheduled-at", "none"),
    [inspectionQuery.data],
  );
  const scheduledTime = useMemo(
    () => _.get(inspectionQuery.data, "data.attributes.scheduled-time", "none"),
    [inspectionQuery.data],
  );
  const checklist = useMemo(() => inspectionQuery.data?.data.attributes.sections, [inspectionQuery.data]);
  const status = useMemo(() => _.get(inspectionQuery.data, "data.attributes.status"), [inspectionQuery.data]);

  const permitsTypeName = useMemo(() => {
    return inspectionQuery.data?.data.attributes.permit?.permitsType?.name ?? "";
  }, [inspectionQuery.data]);

  const firstName = useMemo(
    () => _.get(inspectionQuery.data?.data.attributes.applicant, "data.attributes[first-name]", ""),
    [inspectionQuery.data],
  );
  const lastName = useMemo(
    () => _.get(inspectionQuery.data?.data.attributes.applicant, "data.attributes[last-name]", ""),
    [inspectionQuery.data],
  );

  const confirmationNumber = useMemo(() => {
    return inspectionQuery.data?.data.attributes.permit?.confirmationNumber?.match(/.{3}/g)?.join("-") ?? "";
  }, [inspectionQuery.data]);

  const addressData = inspectionQuery.data?.data.attributes.address;
  const showFlag = !!agencyCountry && addressData?.countryId !== agencyCountry?.id; // Only display flag if country differs from agency country
  const address = addressData ? (
    <InspectionFormattedAddress
      address={addressData.address}
      address2={addressData.address2 ?? null}
      city={addressData.city}
      state={addressData.state}
      zip={addressData.zip}
      countryId={showFlag ? addressData.countryId : null}
    />
  ) : null;
  const applicant = `${firstName} ${lastName}`;
  const queryCache = useQueryCache();

  const [failInspection, failInspectionState] = useMutation(putInspectionFail(networkHeaders, parseInt(id)), {
    onSuccess: () => queryCache.invalidateQueries(),
  });
  const [passInspection, passInspectionState] = useMutation(putInspectionPass(networkHeaders, parseInt(id)), {
    onSuccess: () => queryCache.invalidateQueries(),
  });
  const isLoading = passInspectionState.isLoading || failInspectionState.isLoading;
  const isSmallScreen = useMedia({ query: "(max-width: 767px)" });
  const [isOpenModal, setIsOpenModal] = useState(false);
  const handleOpenModal = () => {
    setIsOpenModal(true);
  };
  const onCloseModal = () => {
    setIsOpenModal(false);
  };
  const onSubmitModal = () => {
    setIsOpenModal(false);
  };

  const handleFailInspection = async (comment: string) => {
    await failInspection({
      comment,
    });
  };
  const handlePassInspection = async (comment: string) => {
    await passInspection({
      comment,
    });
  };

  // Setting the title of the browser tab
  document.title = `${inspectionTitle} - ${confirmationNumber}`;

  const contextRef = useRef<HTMLDivElement | null>(null);

  return (
    <div ref={contextRef}>
      <InspectionModal
        open={isOpenModal}
        openModal={setIsOpenModal}
        onClose={onCloseModal}
        onSubmit={onSubmitModal}
        checklist={checklist}
        onPass={handlePassInspection}
        onFail={handleFailInspection}
      />
      <Grid className='tw-mx-0 tw-px-0'>
        <Grid.Column mobile={16} tablet={16} computer={16} className='tw-m-0 tw-pb-0' style={{ zIndex: 1 }}>
          <PermitTabs />
        </Grid.Column>
        <Grid.Column className='overview tw-px-6 tw-pt-0' mobile={16} tablet={6} computer={4}>
          <Sticky active={!isSmallScreen} context={contextRef}>
            <InspectionPanel
              id={id}
              inspectionTitle={inspectionTitle}
              permitsTypeName={permitsTypeName}
              address={address}
              applicant={applicant}
              confirmationNumber={confirmationNumber}
              recordId={recordId}
              site={site}
              assignee={assignee}
              scheduledAt={scheduledAt}
              scheduledTime={scheduledTime}
              status={status}
              openModal={handleOpenModal}
            />
          </Sticky>
        </Grid.Column>
        <Dimmer active={isLoading}>
          <Loader />
        </Dimmer>
        <Grid.Column mobile={16} tablet={10} computer={12} className='tw-mt-2'>
          <Segment basic className='tw-pt-1'>
            <StaffInspectionSummary id={id} />
            <Checklist inspectionId={id} />
          </Segment>
        </Grid.Column>
      </Grid>
    </div>
  );
};

interface ChecklistProps {
  inspectionId: string | number;
}

type CheckListDisplayOption = (typeof CheckListDisplayOptions)[keyof typeof CheckListDisplayOptions];
const CheckListDisplayOptions = {
  ALL: "ALL",
  INCOMPLETE: "INCOMPLETE",
  FAILED: "FAILED",
  PASSED: "PASSED",
} as const;

const InspectionItemStatus = {
  PASSED: "passed",
  FAILED: "failed",
  INCOMPLETE: null,
} as const;

function Checklist({ inspectionId }: ChecklistProps) {
  const { inspectionQuery } = useInspection({ id: inspectionId });

  const [checkListDisplay, setCheckListDisplay] = useState<CheckListDisplayOption>(CheckListDisplayOptions.ALL);

  const inspectionTypeId = inspectionQuery.data?.data.attributes["inspections-types-id"];
  const inspectionStatus = inspectionQuery.data?.data.attributes.status;

  const { data: inspectionTypeData } = useInspectionType({ id: Number(inspectionTypeId), enabled: !!inspectionTypeId });

  const inspectionTypeSections = inspectionTypeData?.included ?? [];
  const inspectionSectionTypesMap = InspectionChecklistUtils.getInspectionSectionTypesMap(inspectionTypeSections);

  const parentId = inspectionQuery.data?.data.attributes.parentId;

  const defaultChecklist = inspectionQuery.data?.data.attributes.sections ?? [];
  const previouslyIncompleteChecklist = getPreviouslyIncompleteChecklist(inspectionQuery.data);
  const previouslyFailedChecklist = getPreviouslyFailedChecklist(inspectionQuery.data);
  const previouslyPassedChecklist = getPreviouslyPassedChecklist(inspectionQuery.data);

  let checklist = defaultChecklist;
  if (checkListDisplay === CheckListDisplayOptions.FAILED) {
    checklist = previouslyFailedChecklist;
  } else if (checkListDisplay === CheckListDisplayOptions.PASSED) {
    checklist = previouslyPassedChecklist;
  } else if (checkListDisplay === CheckListDisplayOptions.INCOMPLETE) {
    checklist = previouslyIncompleteChecklist;
  }

  const { passInspectionItem } = useInspectionItemPassMutation();
  const { failInspectionItem } = useInspectionItemFailMutation();

  const handlePassItem = async (id: string, sectionId: string, itemId: string) => {
    passInspectionItem(id, sectionId, itemId);
  };
  const handleFailItem = async (id: string, sectionId: string, itemId: string) => {
    failInspectionItem(id, sectionId, itemId);
  };
  const allItemCount = defaultChecklist.flatMap((section) => section.data.attributes.items).length;
  const previouslyIncompleteItemCount = previouslyIncompleteChecklist.flatMap(
    (section) => section.data.attributes.items,
  ).length;
  const previouslyPassedItemCount = previouslyPassedChecklist.flatMap(
    (section) => section.data.attributes.items,
  ).length;
  const previouslyFailedItemCount = previouslyFailedChecklist.flatMap(
    (section) => section.data.attributes.items,
  ).length;

  return (
    <>
      {!!parentId && parentId.toString() !== inspectionId.toString() && (
        <div className='tw-flex tw-justify-end'>
          <div>
            <Button
              className={classNames({
                "tw-bg-[#007AC61A] tw-text-blue": checkListDisplay === CheckListDisplayOptions.ALL,
                "tw-mb-3 tw-py-3": true,
              })}
              circular
              onClick={() => setCheckListDisplay(CheckListDisplayOptions.ALL)}
            >
              All ({allItemCount})
            </Button>
            <Button
              className={classNames({
                "tw-bg-[#007AC61A] tw-text-blue": checkListDisplay === CheckListDisplayOptions.PASSED,
                "tw-mb-3 tw-py-3": true,
              })}
              circular
              onClick={() => setCheckListDisplay(CheckListDisplayOptions.PASSED)}
            >
              Passed ({previouslyPassedItemCount})
            </Button>
            <Button
              className={classNames({
                "tw-bg-[#007AC61A] tw-text-blue": checkListDisplay === CheckListDisplayOptions.FAILED,
                "tw-mb-3 tw-py-3": true,
              })}
              circular
              onClick={() => setCheckListDisplay(CheckListDisplayOptions.FAILED)}
            >
              Failed ({previouslyFailedItemCount})
            </Button>
            <Button
              className={classNames({
                "tw-bg-[#007AC61A] tw-text-blue": checkListDisplay === CheckListDisplayOptions.INCOMPLETE,
                "tw-mb-3 tw-py-3": true,
              })}
              circular
              onClick={() => setCheckListDisplay(CheckListDisplayOptions.INCOMPLETE)}
            >
              Incomplete ({previouslyIncompleteItemCount})
            </Button>
          </div>
        </div>
      )}
      {checklist && checklist.length === 0 && (
        <div>This inspection does not have any {checkListDisplay.toLowerCase()} inspection items</div>
      )}
      {checklist &&
        checklist.length > 0 &&
        checklist.map((section) => {
          const sectionTypeId = section?.data?.attributes?.["inspections-types-sections-id"]?.toString() ?? undefined;
          const sectionType = sectionTypeId !== undefined ? inspectionSectionTypesMap[sectionTypeId] : undefined;
          return (
            <InspectionSection
              key={section.data.id}
              section={section.data.attributes}
              sectionType={sectionType}
              inspectionStatus={inspectionStatus}
              sectionId={section.data.id}
              inspectionId={inspectionId}
              handlePassItem={handlePassItem}
              handleFailItem={handleFailItem}
              user={"staff"}
            />
          );
        })}
    </>
  );
}

function getPreviouslyIncompleteChecklist(
  inspectionData: InspectionGetByIdRes | undefined,
): InspectionGetByIdRes["data"]["attributes"]["sections"] {
  return (
    inspectionData?.data.attributes.sections
      .map((section) => {
        const updatedSection = {
          data: {
            ...section.data,
            attributes: {
              ...section.data.attributes,
              items: section.data.attributes.items.filter((item) => {
                return (
                  item.data.attributes.status === InspectionItemStatus.INCOMPLETE &&
                  item.data.attributes["previous-item-status"] === InspectionItemStatus.INCOMPLETE
                );
              }),
            },
          },
        };
        return updatedSection;
      })
      .filter((section) => {
        return section.data.attributes.items.length > 0;
      }) ?? []
  );
}

function getPreviouslyFailedChecklist(
  inspectionData: InspectionGetByIdRes | undefined,
): InspectionGetByIdRes["data"]["attributes"]["sections"] {
  return (
    inspectionData?.data.attributes.sections
      .map((section) => {
        const updatedSection = {
          data: {
            ...section.data,
            attributes: {
              ...section.data.attributes,
              items: section.data.attributes.items.filter((item) => {
                return (
                  item.data.attributes.status === InspectionItemStatus.FAILED ||
                  (!item.data.attributes.status &&
                    item.data.attributes["previous-item-status"] === InspectionItemStatus.FAILED)
                );
              }),
            },
          },
        };
        return updatedSection;
      })
      .filter((section) => {
        return section.data.attributes.items.length > 0;
      }) ?? []
  );
}

function getPreviouslyPassedChecklist(
  inspectionData: InspectionGetByIdRes | undefined,
): InspectionGetByIdRes["data"]["attributes"]["sections"] {
  return (
    inspectionData?.data.attributes.sections
      .map((section) => {
        const updatedSection = {
          data: {
            ...section.data,
            attributes: {
              ...section.data.attributes,
              items: section.data.attributes.items.filter((item) => {
                return (
                  item.data.attributes.status === InspectionItemStatus.PASSED ||
                  (!item.data.attributes.status &&
                    item.data.attributes["previous-item-status"] === InspectionItemStatus.PASSED)
                );
              }),
            },
          },
        };
        return updatedSection;
      })
      .filter((section) => {
        return section.data.attributes.items.length > 0;
      }) ?? []
  );
}

/**
 * It's probably better than nesting utility functions inside of components
 */
export class InspectionChecklistUtils {
  /**
   * Converts the given section types array into a mapping of inspection section type ids to
   * inspection section type objects.
   *
   * @param inspectionSectionTypes the inspections section type objects, in an array
   * @returns the mapping of ids to inspection section type objects
   */
  static getInspectionSectionTypesMap(
    inspectionSectionTypes: InspectionSectionType[],
  ): Record<string, InspectionSectionType> {
    const inspectionSectionTypesMap: Record<string, InspectionSectionType> = {};
    inspectionSectionTypes.forEach((inspectionSectionType) => {
      if (inspectionSectionType.id === undefined) return;
      inspectionSectionTypesMap[inspectionSectionType.id.toString()] = inspectionSectionType;
    });
    return inspectionSectionTypesMap;
  }

  /**
   * Gets the id of the inspection-section element for a given section
   *
   * @param sectionId the id of the section (from the payload)
   * @returns the id to assign to the section's html element
   */
  static getInspectionSectionElementId(sectionId: string | number): string {
    return `inspection-section-${sectionId}`;
  }
}

interface InspectionFormattedAddressProps {
  address: string;
  address2?: string | null;
  state: string;
  city: string;
  zip: string;
  countryId?: string | null;
}
function InspectionFormattedAddress({
  address,
  address2,
  state,
  city,
  zip,
  countryId,
}: InspectionFormattedAddressProps) {
  return (
    <div>
      <div className='tw-font-[700]'>{makeFullAddress(address, address2)}</div>
      <div className='tw-font-[400]'>{makeFullAddress(null, null, city, state, zip, null, countryId ?? null)}</div>
    </div>
  );
}

export default InspectionChecklist;
